import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const PrivacyPolicy = () => {
  useSEO({
    title: 'Privacy Policy',
    description: 'Read the Family Wishlist Privacy Policy to learn how we protect your personal account info and wishlists, and understand our clear commitments to security and data ownership.',
    keywords: 'family wishlist privacy, data protection, secure gift sharing, privacy policy',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'PrivacyPolicy',
      'name': 'Family Wishlist Privacy Policy',
      'description': 'Information on how personal data, wishlists, and account information is handled on Family Wishlist.',
      'url': `${window.location.origin}/privacy-policy`
    }
  });

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xmas-green hover:text-xmas-red font-bold transition-colors"
        >
          <ArrowLeft size={20} /> Home
        </Link>
      </div>
      <h1 className="text-4xl font-bold text-xmas-red mb-8 text-center">Privacy Policy</h1>

      <div className="prose prose-slate max-w-none space-y-6 text-gray-700">
        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">1. Introduction</h2>
          <p>
            Welcome to Family Wishlist. Your privacy is important to us. This Privacy Policy explains how we collect,
            use, and protect your information when you use our service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">2. Data Collection</h2>
          <p>
            We collect the following information to provide and improve our service:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Information:</strong> Your name and email address are stored when you register to identify you and allow you to log in.</li>
            <li><strong>Wishlist Items:</strong> Any items, names, and links you add to your wishlist are stored so they can be shared with your family group.</li>
            <li><strong>Security Data:</strong> We may collect your IP address for security purposes, such as rate-limiting and preventing automated abuse of the platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">3. Data Visibility, Access, & System Administration</h2>
          <div className="space-y-4">
            <p>
              Please be aware of the following visibility conditions regarding the data stored on this platform (including wishlist items, group names, user accounts, and links):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Single Administrator Access:</strong> To facilitate database maintenance, technical support, troubleshooting, and overall platform administration, a <strong>single system administrator</strong> has direct technical visibility and read/write access to all backend database records. No other third parties, employees, or developers have access to this data.
              </li>
              <li>
                <strong>No Absolute Security Guarantee:</strong> While we implement standard, robust security measures to safeguard your information, no system is entirely impenetrable. No method of electronic transmission or database storage can be guaranteed to be 100% secure.
              </li>
              <li>
                <strong>Assumption of Risk & Release of Liability:</strong> By registering an account and using the Family Wishlist service, you explicitly acknowledge and agree that your wishlist items and account metadata are visible to the system administrator as described. You understand and accept that you submit all data at your own risk. You hereby agree to hold the system administrator, platform owners, and affiliates harmless from any and all liability, claims, or damages arising out of unauthorized access, data breaches, or your choice to post sensitive or confidential information.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">4. Cookies</h2>
          <p>
            Currently, Family Wishlist does not use cookies for tracking or advertising. We use local storage in your
            browser solely to maintain your authentication session.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">5. Sensitive Information</h2>
          <p>
            <strong>Important Disclaimer:</strong> Users should not post any sensitive wishlist items, personal
            identification numbers, or private links. To maintain the safety and integrity of our platform, the
            database items may be checked by the website administrator. Any items deemed sensitive or inappropriate will be removed without notice.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">6. Data Removal Requests</h2>
          <p>
            The website administrator will do their best to respond to any requests to remove data. If you wish to
            have your account or specific data deleted, please contact us at:
          </p>
          <p className="font-bold mt-2">
            <a href="mailto:privacy@family-wishlist.com" className="text-xmas-red hover:underline">
              privacy@family-wishlist.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-xmas-green mb-3">7. Updates to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We encourage you to review this page periodically
            for any changes.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Last updated: July 2026
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
